/**
 * Wheel of Fortune Game
 * Beautiful Pixi.js implementation with GSAP animations
 */

import { Container } from "pixi.js";
import * as PIXI from "pixi.js";
import gsap from "gsap";
import { WheelRenderer } from "./WheelRenderer";
import { PointerRenderer } from "./PointerRenderer";
import type { GameOutcome, GameState, DebugOptions, WheelConfig } from "../runtime/types";

export interface WheelGameOptions {
  config: WheelConfig;
  width: number;
  height: number;
  debug?: DebugOptions;
  onStateChange?: (state: GameState) => void;
  onOutcome?: (outcome: GameOutcome) => void;
}

/**
 * Wheel Game
 * Coordinates wheel rendering and spin animation
 */
export class WheelGame extends Container {
  private config: WheelConfig;
  private wheelRenderer: WheelRenderer;
  private pointerRenderer: PointerRenderer;
  private onStateChange?: (state: GameState) => void;
  private onOutcome?: (outcome: GameOutcome) => void;

  private currentState: GameState = "idle";
  private isSpinning: boolean = false;
  private currentRotation: number = 0;

  constructor(options: WheelGameOptions) {
    super();

    this.config = options.config;
    this.onStateChange = options.onStateChange;
    this.onOutcome = options.onOutcome;

    const centerX = options.width / 2;
    const centerY = options.height / 2;
    const radius = Math.min(options.width, options.height) * 0.35;

    // Create wheel renderer
    this.wheelRenderer = new WheelRenderer(this.config, radius);
    this.wheelRenderer.x = centerX;
    this.wheelRenderer.y = centerY;
    this.addChild(this.wheelRenderer);

    // Create pointer renderer
    this.pointerRenderer = new PointerRenderer(this.config.theme || 'classic', radius);
    this.pointerRenderer.x = centerX;
    this.pointerRenderer.y = centerY;
    this.addChild(this.pointerRenderer);

    // Make wheel interactive
    this.wheelRenderer.eventMode = 'static';
    this.wheelRenderer.cursor = 'pointer';
    this.wheelRenderer.on('pointerdown', () => {
      if (!this.isSpinning) {
        this.simulateSpin();
      }
    });
  }

  /**
   * Simulate a spin (for testing)
   */
  private simulateSpin(): void {
    const segments = this.config.segments;
    const totalWeight = segments.reduce((sum, seg) => sum + seg.weight, 0);
    
    // Pick random segment
    let random = Math.random() * totalWeight;
    let selectedSegment = segments[0];
    
    for (const segment of segments) {
      random -= segment.weight;
      if (random <= 0) {
        selectedSegment = segment;
        break;
      }
    }

    // Calculate target angle
    let cumulativeAngle = 0;
    for (const segment of segments) {
      const segmentAngle = (segment.weight / totalWeight) * 360;
      
      if (segment.outcomeKey === selectedSegment.outcomeKey) {
        const targetAngle = cumulativeAngle + segmentAngle / 2;
        
        this.spin({
          outcomeKey: selectedSegment.outcomeKey,
          outcomeValue: { label: selectedSegment.label },
          targetAngle,
          totalRotation: 5 * 360 + targetAngle
        });
        break;
      }
      
      cumulativeAngle += segmentAngle;
    }
  }

  /**
   * Spin the wheel to a predetermined outcome
   */
  public spin(outcome: GameOutcome): void {
    if (this.isSpinning) {
      console.warn('[WheelGame] Spin already in progress');
      return;
    }

    this.isSpinning = true;
    this.setState("playing");

    const wheel = this.wheelRenderer.getWheel();
    const duration = (this.config.spinDuration || 4000) / 1000;
    const targetRotation = (outcome.totalRotation || 1800) * (Math.PI / 180);

    // Find winning segment index
    const winningSegmentIndex = this.findSegmentIndex(outcome.outcomeKey);

    // Perform spin animation with GSAP
    gsap.to(wheel, {
      rotation: this.currentRotation + targetRotation,
      duration,
      ease: 'power4.out',
      onComplete: () => {
        this.currentRotation = (this.currentRotation + targetRotation) % (Math.PI * 2);
        this.isSpinning = false;
        this.setState("resolved");
        
        // Win moment celebration
        this.celebrateWin(winningSegmentIndex);
        
        if (this.onOutcome) {
          this.onOutcome(outcome);
        }
      }
    });
  }

  /**
   * Find segment index by outcome key
   */
  private findSegmentIndex(outcomeKey: string): number {
    return this.config.segments.findIndex(seg => seg.outcomeKey === outcomeKey);
  }

  /**
   * Win moment celebration effects
   */
  private celebrateWin(segmentIndex: number): void {
    if (segmentIndex < 0) return;

    // Highlight winning segment
    this.wheelRenderer.highlightWinningSegment(segmentIndex);

    // Pointer pulse
    gsap.to(this.pointerRenderer.scale, {
      x: 1.2,
      y: 1.2,
      duration: 0.3,
      yoyo: true,
      repeat: 2,
      ease: 'power2.inOut'
    });

    // Light confetti burst
    this.createConfetti();
  }

  /**
   * Create simple confetti particles
   */
  private createConfetti(): void {
    const confettiContainer = new Container();
    confettiContainer.x = this.wheelRenderer.x;
    confettiContainer.y = this.wheelRenderer.y;
    this.addChild(confettiContainer);

    const colors = [0xFFD700, 0xFF6B9D, 0x4ECDC4, 0xFFE66D, 0x95E1D3];
    const particleCount = 30;

    for (let i = 0; i < particleCount; i++) {
      const particle = new PIXI.Graphics();
      const color = colors[Math.floor(Math.random() * colors.length)];
      
      particle.beginFill(color);
      particle.drawCircle(0, 0, Math.random() * 4 + 2);
      particle.endFill();

      particle.x = 0;
      particle.y = 0;

      confettiContainer.addChild(particle);

      // Random trajectory
      const angle = Math.random() * Math.PI * 2;
      const distance = Math.random() * 150 + 50;
      const targetX = Math.cos(angle) * distance;
      const targetY = Math.sin(angle) * distance - 50; // Bias upward

      gsap.to(particle, {
        x: targetX,
        y: targetY,
        alpha: 0,
        duration: 1 + Math.random() * 0.5,
        ease: 'power2.out'
      });
    }

    // Remove confetti container after animation
    setTimeout(() => {
      this.removeChild(confettiContainer);
    }, 1500);
  }

  private setState(state: GameState): void {
    this.currentState = state;
    if (this.onStateChange) {
      this.onStateChange(state);
    }
  }

  /**
   * Update method called every frame (required by playground)
   */
  public update(_delta: number): void {
    // Animation is handled by GSAP, no frame-by-frame updates needed
  }

  /**
   * Update configuration
   */
  public updateConfig(config: WheelConfig): void {
    this.config = config;
    
    // Stop any ongoing animations
    const wheel = this.wheelRenderer.getWheel();
    gsap.killTweensOf(wheel);
    
    // Reset rotation
    wheel.rotation = 0;
    this.currentRotation = 0;
    
    // Update renderers with new theme
    this.wheelRenderer.updateConfig(config);
    this.pointerRenderer.updateTheme(config.theme || 'modern');
    
    // Reset state
    this.isSpinning = false;
    this.setState("idle");
  }

  /**
   * Get current state
   */
  public getState(): GameState {
    return this.currentState;
  }

  /**
   * Reset the wheel to initial state
   */
  public reset(): void {
    const wheel = this.wheelRenderer.getWheel();
    gsap.killTweensOf(wheel);
    wheel.rotation = 0;
    this.currentRotation = 0;
    this.isSpinning = false;
    this.setState("idle");
  }

  /**
   * Update debug options (for playground compatibility)
   */
  public updateDebug(_debug: DebugOptions): void {
    // Debug rendering can be added here if needed
  }
}
